import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  getStoredConsent,
  hasStoredConsent,
  normalizeConsent,
  saveConsent,
} from "../../util/tracking";
import classes from "./CookieConsentBanner.module.css";

const initialSelection = {
  analytics: false,
  marketing: false,
};

function Toggle({ checked, onChange, disabled, label }) {
  return (
    <button
      type="button"
      className={`${classes.toggle} ${checked ? classes.toggleOn : ""}`}
      onClick={onChange}
      disabled={disabled}
      aria-label={label}
      aria-pressed={checked}
    >
      <span className={classes.toggleKnob} />
    </button>
  );
}

function CategoryRow({ title, description, checked, onToggle, disabled }) {
  return (
    <div className={classes.categoryRow}>
      <div className={classes.categoryContent}>
        <div className={classes.categoryHeading}>
          <h3 className={classes.categoryTitle}>{title}</h3>
          {disabled && <span className={classes.categoryBadge}>Immer aktiv</span>}
        </div>
        <p className={classes.categoryDescription}>{description}</p>
      </div>
      <Toggle
        checked={checked}
        onChange={onToggle}
        disabled={disabled}
        label={`${title} ${checked ? "deaktivieren" : "aktivieren"}`}
      />
    </div>
  );
}

export default function CookieConsentBanner() {
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [selection, setSelection] = useState(initialSelection);

  useEffect(() => {
    const storedConsent = getStoredConsent();
    if (storedConsent) {
      setSelection({
        analytics: storedConsent.analytics,
        marketing: storedConsent.marketing,
      });
    }

    setIsVisible(!hasStoredConsent());
  }, []);

  useEffect(() => {
    if (!showSettings) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [showSettings]);

  if (!isVisible) return null;

  const handleSave = (consent) => {
    saveConsent(normalizeConsent(consent));
    setIsVisible(false);
  };

  const toggleSelection = (key) => {
    setSelection((currentSelection) => ({
      ...currentSelection,
      [key]: !currentSelection[key],
    }));
  };

  if (showSettings) {
    return (
      <div className={classes.overlay} role="presentation">
        <section
          className={classes.modal}
          aria-labelledby="cookie-settings-title"
          aria-describedby="cookie-settings-description"
        >
          <div className={classes.modalHeader}>
            <h2 id="cookie-settings-title" className={classes.modalTitle}>
              Cookie-Einstellungen
            </h2>
            <button
              type="button"
              className={classes.closeButton}
              onClick={() => setShowSettings(false)}
              aria-label="Schließen"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
                <path d="M4.5 4.5L13.5 13.5M13.5 4.5L4.5 13.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>
          </div>

          <div className={classes.modalBody}>
            <p id="cookie-settings-description" className={classes.modalIntro}>
              Wähle aus, welche Cookies wir verwenden dürfen. Notwendige Cookies sind für den Betrieb der Seite erforderlich. Mehr in unserer{" "}
              <Link className={classes.link} to="/datenschutz">
                Datenschutzerklärung
              </Link>
              .
            </p>

            <CategoryRow
              title="Notwendige Cookies"
              description="Grundfunktionen wie Login, Sicherheit und Sitzungsverwaltung. Diese Cookies lassen sich nicht deaktivieren."
              checked
              onToggle={() => {}}
              disabled
            />
            <CategoryRow
              title="Analyse-Cookies"
              description="Helfen uns zu verstehen, wie die Website genutzt wird – anonymisiert und aggregiert."
              checked={selection.analytics}
              onToggle={() => toggleSelection("analytics")}
            />
            <CategoryRow
              title="Marketing-Cookies"
              description="Werden verwendet, um relevante Inhalte und Werbung über Websites hinweg auszuspielen."
              checked={selection.marketing}
              onToggle={() => toggleSelection("marketing")}
            />
          </div>

          <div className={classes.modalFooter}>
            <button
              type="button"
              className={classes.secondaryButton}
              onClick={() => handleSave({ analytics: false, marketing: false })}
            >
              Nur notwendige
            </button>
            <button
              type="button"
              className={classes.primaryButton}
              onClick={() => handleSave(selection)}
            >
              Auswahl speichern
            </button>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div
      className={classes.bannerContainer}
      role="dialog"
      aria-labelledby="cookie-consent-title"
      aria-describedby="cookie-consent-description"
    >
      <section className={classes.banner}>
        <h2 id="cookie-consent-title" className={classes.bannerTitle}>
          Hilf uns, dein Erlebnis zu verbessern
        </h2>
        <p id="cookie-consent-description" className={classes.bannerDescription}>
          Einige Cookies sind notwendig, damit stu funktioniert. Weitere Cookies kannst du jederzeit aktivieren oder deaktivieren. Mehr dazu in der{" "}
          <Link className={classes.link} to="/datenschutz">
            Datenschutzerklärung
          </Link>
          .
        </p>

        <div className={classes.bannerActions}>
          <button
            type="button"
            className={`${classes.primaryButton} ${classes.fullWidth}`}
            onClick={() => handleSave({ analytics: true, marketing: true })}
          >
            Alle akzeptieren
          </button>
          <div className={classes.bannerSecondaryRow}>
            <button
              type="button"
              className={`${classes.secondaryButton} ${classes.fullWidth}`}
              onClick={() => handleSave({ analytics: false, marketing: false })}
            >
              Nur notwendige
            </button>
            <button
              type="button"
              className={`${classes.secondaryButton} ${classes.fullWidth}`}
              onClick={() => setShowSettings(true)}
            >
              Einstellungen
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
